/**
 * As fórmulas da folha: data de pagamento, calculadora, recorrência, Outros
 * Bancos, folha fechada, ordem e recibo.
 *
 * Papel: aqui ficam o dia de hoje no fuso do escritório, os feriados e o dia de
 * pagamento da folha, os itens e os subtotais da Calculadora Petrella, os itens
 * que a recorrência automática gera, as descrições que as importações e a
 * calculadora gravam nos lançamentos, os dias e os itens de cada cartão de
 * Outros Bancos, a fotografia da folha fechada e os itens da Ordem de Pagamento
 * e do Recibo. O navegador e a API usam as mesmas.
 *
 * Como usar: todo arredondamento daqui é o da origem, `arredondarComoAOrigem`
 * (`Math.round(valor * 100) / 100`), e não `arredondarCentavos`. Os dois
 * discordam no meio centavo em ponto flutuante — `1.005` vira `1` aqui e `1.01`
 * lá —, e trocar um pelo outro faria a calculadora gravar um centavo diferente
 * do que a origem gravava para a mesma diária.
 *
 * Os feriados daqui são só os nacionais e os móveis que a origem conta. **Não é**
 * o `feriadosDoAno` do calendário de escala, que tem os municipais: o dia de
 * pagamento sairia um dia antes em todo mês com feriado da cidade.
 */
import { domingoDePascoa } from './calendario';
import { type AjudaCustoDoFavorecido, type TipoItemAjudaCusto } from './financas';

/** Os meses por extenso, como o `MESES` da origem. */
export const MESES_POR_EXTENSO = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/**
 * A subcategoria de "Despesas com Pessoal" de cada tipo de ajuda de custo.
 *
 * É também o rótulo do tipo na calculadora, no perfil e no quadro de férias.
 */
export const SUBCATEGORIA_DO_TIPO: Readonly<Record<TipoItemAjudaCusto, string>> = {
  custo: 'Ajuda de Custo',
  diarias: 'Diárias',
  medica: 'Auxílio plano de saúde',
  inss: 'Devolução de INSS',
};

/**
 * A subcategoria do lançamento de auxílio alimentação, a que mostra o 🧾 na linha
 * de Lançamentos e a que a recorrência grava.
 */
export const SUBCATEGORIA_DA_ALIMENTACAO = 'Benefícios alimentação';

/**
 * O começo da descrição do auxílio alimentação. A recorrência também o procura
 * para não duplicar o mês de um lançamento gravado em outra subcategoria.
 */
export const PREFIXO_DA_ALIMENTACAO = 'Auxílio alimentação';

/**
 * A subcategoria do lançamento da aba Diaristas da Calculadora Petrella, a que
 * mostra o 🧾 na linha de Lançamentos. O recibo sai do lançamento avulso, como o
 * da alimentação, com a descrição gravada como único item.
 */
export const SUBCATEGORIA_DA_DIARISTA = 'Diaristas Passadeiras';

/** O fuso em que o escritório vive o dia. */
const FUSO_DO_ESCRITORIO = 'America/Sao_Paulo';

/** O formatador que escreve uma data como `AAAA-MM-DD` no fuso do escritório. */
const DIA_NO_ESCRITORIO = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO_DO_ESCRITORIO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * O arredondamento da origem: `Math.round(valor * 100) / 100`.
 *
 * @param valor - O valor em reais.
 * @returns O valor com duas casas, com o meio centavo decidido pelo ponto
 *   flutuante, como a origem decidia.
 */
export function arredondarComoAOrigem(valor: number): number {
  return Math.round(valor * 100) / 100;
}

/**
 * O dia de um instante no fuso do escritório.
 *
 * Quem chama é a API, com `new Date()`, para o `fechado_em` da folha, o
 * `importado_em` do salário e da guia, a trava diária da recorrência e o recibo
 * sem data. Às 22 h do último dia do mês, em Brasília, o UTC já está no mês
 * seguinte, e um `toISOString` gravaria a folha fechada no dia errado.
 *
 * @param instante - O instante; a API passa o de agora.
 * @returns A data em `AAAA-MM-DD`.
 */
export function diaNoEscritorio(instante: Date): string {
  return DIA_NO_ESCRITORIO.format(instante);
}

/** A data `AAAA-MM-DD` de um instante em UTC. */
function isoDeUtc(data: Date): string {
  return data.toISOString().slice(0, 10);
}

/**
 * Os feriados que adiam o pagamento da folha num ano.
 *
 * @param ano - O ano com quatro dígitos.
 * @returns As datas em `AAAA-MM-DD`: os nove fixos nacionais, as segunda e
 *   terça de Carnaval, a Sexta-feira Santa e Corpus Christi.
 */
export function feriadosDaFolha(ano: number): Set<string> {
  const feriados = new Set<string>();
  for (const [mes, dia] of [
    [1, 1],
    [4, 21],
    [5, 1],
    [9, 7],
    [10, 12],
    [11, 2],
    [11, 15],
    [11, 20],
    [12, 25],
  ] as const) {
    feriados.add(isoDeUtc(new Date(Date.UTC(ano, mes - 1, dia))));
  }
  const pascoa = domingoDePascoa(ano).getTime();
  for (const dias of [-48, -47, -2, 60]) {
    feriados.add(isoDeUtc(new Date(pascoa + dias * 86_400_000)));
  }
  return feriados;
}

/**
 * O dia em que a folha de um mês é paga.
 *
 * Parte do dia 30, ou do último dia do mês quando ele é menor, e recua um dia
 * por vez enquanto cair em sábado, domingo ou feriado de `feriadosDaFolha`. A
 * conta é feita em UTC: com a data local, a meia-noite do horário de verão de
 * outro fuso poderia pular ou repetir um dia.
 *
 * @param ano - O ano com quatro dígitos.
 * @param mes - O mês, de 1 a 12.
 * @returns A data em `AAAA-MM-DD`.
 */
export function dataDePagamentoDaFolha(ano: number, mes: number): string {
  const ultimoDia = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  const feriados = feriadosDaFolha(ano);
  const data = new Date(Date.UTC(ano, mes - 1, Math.min(30, ultimoDia)));
  const naoUtil = () =>
    data.getUTCDay() === 0 || data.getUTCDay() === 6 || feriados.has(isoDeUtc(data));
  while (naoUtil()) data.setUTCDate(data.getUTCDate() - 1);
  return isoDeUtc(data);
}

/**
 * O dia de pagamento da folha de uma competência.
 *
 * @param competencia - O mês, em `AAAA-MM`.
 * @returns A data em `AAAA-MM-DD`, por `dataDePagamentoDaFolha`.
 */
export function dataDePagamentoDaCompetencia(competencia: string): string {
  return dataDePagamentoDaFolha(
    Number(competencia.slice(0, 4)),
    Number(competencia.slice(5, 7)),
  );
}

/**
 * Escreve a competência como as descrições e os recibos da origem a escrevem.
 *
 * @param competencia - O mês, em `AAAA-MM`.
 * @returns `Setembro/2026`.
 */
export function mesEAnoPorExtenso(competencia: string): string {
  const nome = MESES_POR_EXTENSO[Number(competencia.slice(5, 7)) - 1] ?? '';
  return `${nome}/${competencia.slice(0, 4)}`;
}

/**
 * Escreve a competência com o mês em dois dígitos.
 *
 * @param competencia - O mês, em `AAAA-MM`.
 * @returns `09/2026`.
 */
export function competenciaComBarra(competencia: string): string {
  return `${competencia.slice(5, 7)}/${competencia.slice(0, 4)}`;
}

/** "dia" ou "dias", pela regra da origem: só o número 1 fica no singular. */
function dias(quantidade: number): string {
  return `${String(quantidade)} dia${quantidade !== 1 ? 's' : ''}`;
}

/* ------------------------------------------------------------- calculadora */

/** Um item que a calculadora, a recorrência ou Outros Bancos calculam. */
export interface ItemCalculado {
  tipo: TipoItemAjudaCusto;
  /** A descrição de recibo, que vira a `descricao` do item do grupo. */
  descricao: string;
  valor: number;
  /** A diária usada; nula nos dois tipos de valor fixo. */
  valorDiaria: number | null;
  /** Os dias usados; nulos nos dois tipos de valor fixo. */
  dias: number | null;
}

/** Um cartão de tipo com diária, marcado. A diária vazia vale zero. */
export interface TipoComDiaria {
  valorDiaria: number | null;
  dias: number;
}

/** Um cartão de tipo de valor fixo, marcado. O valor vazio vale zero. */
export interface TipoFixo {
  valor: number | null;
}

/**
 * A aba "Ajuda de Custo" da calculadora: as férias e os quatro cartões, com
 * `null` no cartão desmarcado.
 */
export interface EntradaDaCalculadora {
  ferias: boolean;
  custo: TipoComDiaria | null;
  diarias: TipoComDiaria | null;
  medica: TipoFixo | null;
  inss: TipoFixo | null;
}

/** O multiplicador do 1/3 de férias. */
const COM_FERIAS = 4 / 3;

/**
 * Os itens que "Criar Lançamentos (Pendente)" grava: um por cartão marcado com
 * valor maior que zero, na ordem custo, diárias, plano de saúde, INSS.
 *
 * O item com diária vale `diária × dias × 4/3` com férias, e o de valor fixo
 * nunca recebe o 1/3. Cada item é arredondado sozinho, como a origem fazia: o
 * total do grupo é a soma dos itens arredondados, e não o total arredondado.
 *
 * @param entrada - Os cartões da aba, como estão à vista.
 * @returns Os itens; vazio quando nenhum cartão marcado tem valor.
 */
export function itensDaCalculadora(entrada: EntradaDaCalculadora): ItemCalculado[] {
  const multiplicador = entrada.ferias ? COM_FERIAS : 1;
  const sufixo = entrada.ferias ? 'férias' : 'trabalhados';
  const itens: ItemCalculado[] = [];

  for (const tipo of ['custo', 'diarias'] as const) {
    const cartao = entrada[tipo];
    if (cartao === null) continue;
    const diaria = cartao.valorDiaria ?? 0;
    const valor = arredondarComoAOrigem(diaria * cartao.dias * multiplicador);
    if (valor > 0) {
      itens.push({
        tipo,
        descricao: `${SUBCATEGORIA_DO_TIPO[tipo]} ${dias(cartao.dias)} ${sufixo}`,
        valor,
        valorDiaria: diaria,
        dias: cartao.dias,
      });
    }
  }
  for (const tipo of ['medica', 'inss'] as const) {
    const cartao = entrada[tipo];
    if (cartao === null) continue;
    const valor = arredondarComoAOrigem(cartao.valor ?? 0);
    if (valor > 0) {
      itens.push({
        tipo,
        descricao: SUBCATEGORIA_DO_TIPO[tipo],
        valor,
        valorDiaria: null,
        dias: null,
      });
    }
  }
  return itens;
}

/** O subtotal de um cartão com diária, antes e depois do 1/3 de férias. */
export interface SubtotalComDiaria {
  base: number;
  final: number;
}

/** O que a aba "Ajuda de Custo" mostra a cada digitação. */
export interface SubtotaisDaCalculadora {
  custo: SubtotalComDiaria;
  diarias: SubtotalComDiaria;
  medica: number;
  inss: number;
  total: number;
}

/**
 * Os subtotais e o total geral à vista, sem arredondar.
 *
 * A origem formata `diária × dias × 4/3` direto, e o centavo de diferença entre
 * o subtotal à vista e o item gravado por `itensDaCalculadora` é dela. O cartão
 * desmarcado vale zero.
 *
 * @param entrada - Os cartões da aba, como estão à vista.
 * @returns A base e o final dos dois cartões com diária, os dois fixos e a soma
 *   dos quatro finais. O acréscimo do quadro de férias é `final − base`.
 */
export function subtotaisDaCalculadora(entrada: EntradaDaCalculadora): SubtotaisDaCalculadora {
  const multiplicador = entrada.ferias ? COM_FERIAS : 1;
  const comDiaria = (cartao: TipoComDiaria | null): SubtotalComDiaria => {
    const base = cartao === null ? 0 : (cartao.valorDiaria ?? 0) * cartao.dias;
    return { base, final: base * multiplicador };
  };
  const custo = comDiaria(entrada.custo);
  const diarias = comDiaria(entrada.diarias);
  const medica = entrada.medica?.valor ?? 0;
  const inss = entrada.inss?.valor ?? 0;
  return { custo, diarias, medica, inss, total: custo.final + diarias.final + medica + inss };
}

/**
 * O valor do lançamento da aba "Diaristas".
 *
 * @param valorDiaria - A diária, com até quatro casas; vazia vale zero.
 * @param quantidade - Os dias trabalhados, que aceitam fração.
 * @returns `diária × dias`, arredondado como a origem.
 */
export function valorDaDiarista(valorDiaria: number | null, quantidade: number): number {
  return arredondarComoAOrigem((valorDiaria ?? 0) * quantidade);
}

/* -------------------------------------------------------------- recorrência */

/**
 * Os itens que a recorrência automática gera num mês: o padrão do perfil, com
 * 30 dias e sem férias.
 *
 * Entra cada tipo habilitado no perfil (o valor não é nulo) cujo item dê mais
 * que zero. É `itensDaCalculadora` com os quatro cartões vindos do perfil.
 *
 * @param ajudaCusto - A ajuda de custo do favorecido.
 * @returns Os itens; vazio quando nenhum tipo habilitado tem valor.
 */
export function itensPadraoDaRecorrencia(ajudaCusto: AjudaCustoDoFavorecido): ItemCalculado[] {
  return itensDaCalculadora({
    ferias: false,
    custo:
      ajudaCusto.custoDiaria === null
        ? null
        : { valorDiaria: ajudaCusto.custoDiaria, dias: 30 },
    diarias:
      ajudaCusto.diariasDiaria === null
        ? null
        : { valorDiaria: ajudaCusto.diariasDiaria, dias: 30 },
    medica: ajudaCusto.medicaValor === null ? null : { valor: ajudaCusto.medicaValor },
    inss: ajudaCusto.inssValor === null ? null : { valor: ajudaCusto.inssValor },
  });
}

/**
 * As competências que a recorrência cobre para um favorecido.
 *
 * @param desde - A competência de `recorrenciaDesde`, em `AAAA-MM`.
 * @param anoDeHoje - O ano de hoje no fuso do escritório.
 * @returns Os meses de `desde` até dezembro de `anoDeHoje`, em ordem; vazio
 *   quando `desde` é de um ano posterior.
 */
export function competenciasDaRecorrencia(desde: string, anoDeHoje: number): string[] {
  const competencias: string[] = [];
  let ano = Number(desde.slice(0, 4));
  let mes = Number(desde.slice(5, 7));
  while (ano <= anoDeHoje) {
    competencias.push(`${String(ano)}-${String(mes).padStart(2, '0')}`);
    mes += 1;
    if (mes > 12) {
      mes = 1;
      ano += 1;
    }
  }
  return competencias;
}

/* ---------------------------------------------------------------- descrições */

/**
 * A descrição de cada lançamento de um grupo de ajuda de custo.
 *
 * @param tipo - O tipo do item.
 * @param favorecido - O nome do favorecido, como ficou gravado.
 * @param competencia - A competência do grupo, em `AAAA-MM`.
 * @returns `Ajuda de Custo — <favorecido> — Setembro/2026`.
 */
export function descricaoDaAjudaDeCusto(
  tipo: TipoItemAjudaCusto,
  favorecido: string,
  competencia: string,
): string {
  return `${SUBCATEGORIA_DO_TIPO[tipo]} — ${favorecido} — ${mesEAnoPorExtenso(competencia)}`;
}

/**
 * A descrição do lançamento de auxílio alimentação de um mês.
 *
 * @param competencia - A competência, em `AAAA-MM`.
 * @returns `Auxílio alimentação 11/2026`.
 */
export function descricaoDoAuxilioAlimentacao(competencia: string): string {
  return `${PREFIXO_DA_ALIMENTACAO} ${competencia.slice(5, 7)}/${competencia.slice(0, 4)}`;
}

/**
 * A descrição do lançamento da aba "Diaristas".
 *
 * @param favorecido - O nome da diarista, como foi digitado.
 * @param quantidade - Os dias trabalhados, escritos como número do JavaScript
 *   (`2.5`, e não `2,5`), como a origem escrevia.
 * @param competencia - A competência, em `AAAA-MM`.
 * @returns `Diaristas Passadeiras — <favorecido> — 4 dias — Setembro/2026`.
 */
export function descricaoDaDiarista(
  favorecido: string,
  quantidade: number,
  competencia: string,
): string {
  return `Diaristas Passadeiras — ${favorecido} — ${dias(quantidade)} — ${mesEAnoPorExtenso(competencia)}`;
}

/**
 * A descrição de cada lançamento da folha importada do PDF.
 *
 * @param competencia - A competência da folha, em `AAAA-MM`.
 * @param favorecido - O nome do funcionário.
 * @returns `Folha de pagamento 09/2026 — <favorecido>`.
 */
export function descricaoDaFolha(competencia: string, favorecido: string): string {
  return `Folha de pagamento ${competenciaComBarra(competencia)} — ${favorecido}`;
}

/**
 * A descrição de cada lançamento da guia DAE.
 *
 * @param competencia - O período de apuração, em `AAAA-MM`, ou `null` quando o
 *   PDF não o trazia.
 * @param denominacao - A denominação da linha da guia.
 * @returns `Guia DAE 09/2026 — <denominação>`, ou `Guia DAE — <denominação>`.
 */
export function descricaoDaGuiaDae(competencia: string | null, denominacao: string): string {
  return `Guia DAE${competencia === null ? '' : ` ${competenciaComBarra(competencia)}`} — ${denominacao}`;
}

/* ------------------------------------------------------------ outros bancos */

/** Os dias de um mês de Outros Bancos como estão gravados; nulo é não gravado. */
export interface DiasDeOutrosBancos {
  custo: number | null;
  diarias: number | null;
}

/** Os dias que valem num cartão de Outros Bancos: sempre um número. */
export interface DiasEfetivos {
  custo: number;
  diarias: number;
}

/**
 * O mês anterior, com dezembro do ano anterior antes de janeiro.
 *
 * @param competencia - O mês, em `AAAA-MM`.
 * @returns O mês anterior, em `AAAA-MM`.
 */
export function competenciaAnterior(competencia: string): string {
  const ano = Number(competencia.slice(0, 4));
  const mes = Number(competencia.slice(5, 7));
  return mes === 1
    ? `${String(ano - 1)}-12`
    : `${String(ano)}-${String(mes - 1).padStart(2, '0')}`;
}

/** Os dias de uma fonte, quando ela tem algum dos dois gravado. */
function diasDaFonte(fonte: DiasDeOutrosBancos | null): DiasEfetivos | null {
  if (fonte === null || (fonte.custo === null && fonte.diarias === null)) return null;
  return { custo: fonte.custo ?? 30, diarias: fonte.diarias ?? 30 };
}

/**
 * Os dias sugeridos de um cartão de Outros Bancos.
 *
 * Vale a primeira fonte que tiver algum dos dois dias gravado, e nela o que
 * estiver nulo vale 30: a linha do mês, a linha do mês anterior, a folha fechada
 * do mês anterior. Sem nenhuma, os dois valem 30. A sugestão não grava nada: ela
 * só aparece no campo, e quem grava é quem edita.
 *
 * @param doMes - Os dias da linha do mês aberto, ou `null` sem linha.
 * @param doMesAnterior - Os dias da linha de `competenciaAnterior`, ou `null`.
 * @param daFolhaFechadaAnterior - Os dias usados da folha fechada de
 *   `competenciaAnterior`, ou `null`.
 * @returns Os dois dias que o cartão mostra.
 */
export function diasEfetivos(
  doMes: DiasDeOutrosBancos | null,
  doMesAnterior: DiasDeOutrosBancos | null,
  daFolhaFechadaAnterior: DiasDeOutrosBancos | null,
): DiasEfetivos {
  return (
    diasDaFonte(doMes) ??
    diasDaFonte(doMesAnterior) ??
    diasDaFonte(daFolhaFechadaAnterior) ?? { custo: 30, diarias: 30 }
  );
}

/**
 * As iniciais de quem debita a conta apartada.
 *
 * @param debitoDe - O "A débito de" do perfil.
 * @returns A primeira letra de cada palavra, inclusive "de", em maiúsculas; vazio
 *   sem nome.
 */
export function iniciaisDoDebito(debitoDe: string | null): string {
  return (debitoDe ?? '')
    .trim()
    .split(/\s+/)
    .map((palavra) => palavra.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * O título do cartão de folha e da folha fechada.
 *
 * @param debitoDe - O "A débito de" do perfil.
 * @returns `Folha de Pagamento` seguido das iniciais, quando houver.
 */
export function tituloDaFolha(debitoDe: string | null): string {
  const iniciais = iniciaisDoDebito(debitoDe);
  return iniciais === '' ? 'Folha de Pagamento' : `Folha de Pagamento ${iniciais}`;
}

/**
 * Quem paga o recibo da ajuda de custo de um cartão de Outros Bancos.
 *
 * @param debitoDe - O "A débito de" do perfil, que é quem emprega o favorecido.
 * @param pagadorPadrao - O pagador padrão da casa, usado quando o perfil não
 *   diz quem debita.
 * @returns O "A débito de" em maiúsculas, como o nome do pagador sai nos
 *   demais recibos; o pagador padrão sem ele.
 */
export function pagadorDoCartao(debitoDe: string | null, pagadorPadrao: string): string {
  const debito = (debitoDe ?? '').trim();
  return debito === '' ? pagadorPadrao : debito.toLocaleUpperCase('pt-BR');
}

/**
 * Os itens de ajuda de custo de um cartão de Outros Bancos.
 *
 * O item com diária usa os dias do mês, e não 30. Ele continua no cartão com 0
 * dia, valendo zero, para que o campo de dias continue à vista e o 0 possa ser
 * desfeito; os demais itens que dão zero ficam de fora. Quem monta a ordem, o
 * recibo e a folha fechada deixa de fora todo item que vale zero.
 *
 * @param ajudaCusto - A ajuda de custo do favorecido.
 * @param diasDoMes - Os dias efetivos do cartão, por `diasEfetivos`.
 * @returns Os itens na ordem custo, diárias, plano de saúde, INSS.
 */
export function itensDeOutrosBancos(
  ajudaCusto: AjudaCustoDoFavorecido,
  diasDoMes: DiasEfetivos,
): ItemCalculado[] {
  const itens: ItemCalculado[] = [];
  for (const [tipo, diaria] of [
    ['custo', ajudaCusto.custoDiaria],
    ['diarias', ajudaCusto.diariasDiaria],
  ] as const) {
    if (diaria === null) continue;
    const quantidade = diasDoMes[tipo];
    const valor = arredondarComoAOrigem(diaria * quantidade);
    if (valor > 0 || quantidade === 0) {
      itens.push({
        tipo,
        descricao: `${SUBCATEGORIA_DO_TIPO[tipo]} — ${dias(quantidade)} trabalhados`,
        valor,
        valorDiaria: diaria,
        dias: quantidade,
      });
    }
  }
  const fixos = itensPadraoDaRecorrencia({
    ...ajudaCusto,
    custoDiaria: null,
    diariasDiaria: null,
  });
  return [...itens, ...fixos];
}

/** Uma linha da guia DAE como a folha fechada e o cartão a leem. */
export interface LinhaDaGuia {
  denominacao: string;
  subcategoria: string;
  valor: number;
}

/**
 * O total de uma lista de valores, arredondado como a origem.
 *
 * É o total da guia DAE, da folha importada e da folha fechada, que não são
 * coluna no banco.
 *
 * @param itens - Os itens com valor.
 * @returns A soma, arredondada uma vez no fim.
 */
export function totalArredondado(itens: readonly { valor: number }[]): number {
  return arredondarComoAOrigem(itens.reduce((soma, item) => soma + item.valor, 0));
}

/**
 * O "Total" do rodapé do cartão de Outros Bancos.
 *
 * @param salario - O salário líquido do mês; nulo vale zero.
 * @param itens - Os itens de `itensDeOutrosBancos`.
 * @param totalDaGuia - O total da guia DAE do mês; zero sem guia.
 * @returns `salário + Σ ajuda de custo + guia`, sem arredondar, como a origem.
 */
export function totalDoCartao(
  salario: number | null,
  itens: readonly ItemCalculado[],
  totalDaGuia: number,
): number {
  return (salario ?? 0) + itens.reduce((soma, item) => soma + item.valor, 0) + totalDaGuia;
}

/** Um item da folha fechada, com os subitens da linha da guia DAE. */
export interface ItemDeFolhaFechada {
  nome: string;
  valor: number;
  subitens: { nome: string; valor: number }[];
}

/**
 * A fotografia que "Fechar Folha" grava.
 *
 * Os itens, na ordem: "Salário líquido", se maior que zero; cada item de ajuda
 * de custo maior que zero, com a descrição como nome; e **uma** linha "Guia
 * DAE" (com " (MM/AAAA)" quando a guia tem competência), cujos subitens são as
 * linhas da guia maiores que zero e cujo valor é a soma arredondada deles — a
 * linha só entra se essa soma for maior que zero.
 *
 * @param salario - O salário líquido do mês; nulo vale zero.
 * @param itensDaAjuda - Os itens de `itensDeOutrosBancos`.
 * @param guia - A guia do mês, com a competência em `AAAA-MM`, ou `null`.
 * @returns Os itens e o total arredondado; itens vazios quando não há nada a
 *   fechar.
 */
export function montarFolhaFechada(
  salario: number | null,
  itensDaAjuda: readonly ItemCalculado[],
  guia: { competencia: string | null; itens: readonly LinhaDaGuia[] } | null,
): { itens: ItemDeFolhaFechada[]; total: number } {
  const itens: ItemDeFolhaFechada[] = [];
  if ((salario ?? 0) > 0)
    itens.push({ nome: 'Salário líquido', valor: salario ?? 0, subitens: [] });
  for (const item of itensDaAjuda) {
    if (item.valor > 0) itens.push({ nome: item.descricao, valor: item.valor, subitens: [] });
  }
  if (guia !== null && guia.itens.length > 0) {
    const subitens = guia.itens
      .filter((linha) => linha.valor > 0)
      .map((linha) => ({ nome: linha.denominacao || linha.subcategoria, valor: linha.valor }));
    const valor = totalArredondado(subitens);
    if (valor > 0) {
      const sufixo =
        guia.competencia === null ? '' : ` (${competenciaComBarra(guia.competencia)})`;
      itens.push({ nome: `Guia DAE${sufixo}`, valor, subitens });
    }
  }
  return { itens, total: totalArredondado(itens) };
}

/** Uma linha de documento: a descrição e o valor. */
export interface LinhaDeDocumento {
  descricao: string;
  valor: number;
}

/**
 * A discriminação da Ordem de Pagamento.
 *
 * A guia DAE não entra, como na origem: a ordem é o que se transfere ao
 * colaborador, e a guia é paga por fora.
 *
 * @param salario - O salário líquido do mês; nulo vale zero.
 * @param itensDaAjuda - Os itens de `itensDeOutrosBancos`.
 * @returns "Salário líquido", se maior que zero, e cada item de ajuda de custo
 *   maior que zero; vazio quando não há o que pagar.
 */
export function itensDaOrdemDePagamento(
  salario: number | null,
  itensDaAjuda: readonly ItemCalculado[],
): LinhaDeDocumento[] {
  const itens: LinhaDeDocumento[] = [];
  if ((salario ?? 0) > 0) itens.push({ descricao: 'Salário líquido', valor: salario ?? 0 });
  for (const item of itensDaAjuda) {
    if (item.valor > 0) itens.push({ descricao: item.descricao, valor: item.valor });
  }
  return itens;
}

/** Um item de recibo antes de destrinchar o 1/3 de férias. */
export interface ItemDoRecibo {
  tipo: TipoItemAjudaCusto | null;
  descricao: string;
  valor: number;
}

/**
 * As linhas do recibo, com o 1/3 de férias destrinchado.
 *
 * O item elegível (tipo nulo, `custo` ou `diarias`) é de férias quando `ferias`
 * é `true`, ou quando `ferias` é nulo e a descrição fala em férias. Com valor
 * maior que zero, ele vira duas linhas: a base, `valor × 3/4`, com a descrição
 * dele, e o acréscimo, `valor − base`, como `1/3 Férias Sobre Ajuda de Custo`
 * (ou `… Sobre Diárias`). As duas somam o valor do item.
 *
 * @param itens - Os itens do grupo, ou os montados para o recibo avulso.
 * @param ferias - `grupo.ferias`, que decide sozinho quando é `true` ou `false`;
 *   `null` no recibo sem grupo, que olha o texto.
 * @returns As linhas na ordem dos itens.
 */
export function itensDoReciboComFerias(
  itens: readonly ItemDoRecibo[],
  ferias: boolean | null,
): LinhaDeDocumento[] {
  return itens.flatMap((item) => {
    const elegivel = item.tipo === null || item.tipo === 'custo' || item.tipo === 'diarias';
    const deFerias =
      elegivel && (ferias === true || (ferias === null && /f[ée]rias/i.test(item.descricao)));
    if (!deFerias || item.valor <= 0)
      return [{ descricao: item.descricao, valor: item.valor }];
    const base = arredondarComoAOrigem((item.valor * 3) / 4);
    const rotulo = item.tipo === 'diarias' ? 'Diárias' : 'Ajuda de Custo';
    return [
      { descricao: item.descricao, valor: base },
      {
        descricao: `1/3 Férias Sobre ${rotulo}`,
        valor: arredondarComoAOrigem(item.valor - base),
      },
    ];
  });
}
